import { createBrowserRouter, Navigate } from 'react-router';
import type { RouteObject } from 'react-router';
import { lazy, Suspense } from 'react';
import { AppLayout } from './layouts/AppLayout';
import { AppShell } from './layouts/AppShell';
import ErrorBoundaryPage from './pages/ErrorBoundaryPage';
import LoadingPage from './pages/LoadingPage';
import { RequireAuth } from './auth';
import { ROUTES } from './constants';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const LoginCallbackPage = lazy(() => import('./pages/LoginCallbackPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'));
const CreateProjectPage = lazy(() => import('./pages/projects/CreateProjectPage'));
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'));

const CreateQuotePage = lazy(() => import('./pages/quotes/CreateQuotePage'));

const AdminPage = lazy(() => import('./pages/admin/AdminPage'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
    return <Suspense fallback={<LoadingPage />}>{children}</Suspense>;
}

const routes: RouteObject[] = [
    // Public routes (no shell)
    {
        path: ROUTES.ROOT,
        element: <AppLayout />,
        errorElement: <ErrorBoundaryPage />,
        children: [
            {
                index: true,
                element: <Navigate to={ROUTES.PROJECTS} replace />,
            },
            {
                path: ROUTES.LOGIN,
                element: (
                    <SuspenseWrapper>
                        <LoginPage />
                    </SuspenseWrapper>
                ),
            },
            {
                path: ROUTES.LOGIN_CALLBACK,
                element: (
                    <SuspenseWrapper>
                        <LoginCallbackPage />
                    </SuspenseWrapper>
                ),
            },
        ],
    },
    // Authenticated shell routes
    {
        element: (
            <RequireAuth>
                <AppShell />
            </RequireAuth>
        ),
        errorElement: <ErrorBoundaryPage />,
        children: [
            // Projects
            {
                path: ROUTES.PROJECTS,
                element: (
                    <SuspenseWrapper>
                        <ProjectsPage />
                    </SuspenseWrapper>
                ),
            },
            {
                path: ROUTES.PROJECTS_NEW,
                element: (
                    <RequireAuth roles={['admin', 'project_admin']}>
                        <SuspenseWrapper>
                            <CreateProjectPage />
                        </SuspenseWrapper>
                    </RequireAuth>
                ),
            },
            {
                path: ROUTES.PROJECT_DETAIL,
                element: (
                    <SuspenseWrapper>
                        <ProjectDetailPage />
                    </SuspenseWrapper>
                ),
            },
            {
                path: ROUTES.PROJECT_QUOTE_NEW,
                element: (
                    <RequireAuth roles={['admin', 'project_admin']}>
                        <SuspenseWrapper>
                            <CreateQuotePage />
                        </SuspenseWrapper>
                    </RequireAuth>
                ),
            },
            // 404 within the authenticated shell
            {
                path: '*',
                element: (
                    <SuspenseWrapper>
                        <NotFoundPage />
                    </SuspenseWrapper>
                ),
            },
            // Admin
            {
                path: ROUTES.ADMIN,
                element: (
                    <RequireAuth roles={['admin']}>
                        <Navigate to={ROUTES.ADMIN_CUSTOMERS} replace />
                    </RequireAuth>
                ),
            },
            {
                path: `${ROUTES.ADMIN}/:tab`,
                element: (
                    <RequireAuth roles={['admin']}>
                        <SuspenseWrapper>
                            <AdminPage />
                        </SuspenseWrapper>
                    </RequireAuth>
                ),
            },
        ],
    },
];

export const router = createBrowserRouter(routes);
